import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useDashboardData } from "../context/DashboardDataContext";
import { formatPct, PATTERN_LABELS } from "../dashboard/analytics";
import { Card } from "../components/ui/Card";
import { KpiCard } from "../components/ui/KpiCard";
import { PageHeader } from "../components/ui/PageHeader";
import { HorizontalBars } from "../components/charts/HorizontalBars";
import { DonutSeverity } from "../components/charts/DonutSeverity";
import { FunnelVertical } from "../components/charts/FunnelVertical";

export function ExecutiveOverview() {
  const { analytics, submissions } = useDashboardData();

  const patternBarItems = useMemo(() => {
    const entries = Object.entries(analytics.patternFrequency).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...entries.map(([, v]) => v));
    return entries.map(([label, value]) => ({
      label: PATTERN_LABELS[label] || label,
      value,
      display: `${((value / max) * 100).toFixed(0)}%`,
    }));
  }, [analytics.patternFrequency]);

  const funnelStages = useMemo(() => {
    const { funnel } = analytics;
    const s = funnel.started || 1;
    const c = funnel.completed;
    const r = funnel.reportViewed;
    const t = funnel.ctaClicked;
    const p = funnel.paid;
    return [
      { label: "Started", count: funnel.started, pct: "100%" },
      {
        label: "Completed",
        count: c,
        pct: formatPct(c / s),
      },
      {
        label: "Report viewed",
        count: r,
        pct: c ? formatPct(r / c) : "—",
      },
      {
        label: "CTA clicked",
        count: t,
        pct: r ? formatPct(t / r) : "—",
      },
      {
        label: "Paid client",
        count: p,
        pct: t ? formatPct(p / Math.max(1, t)) : r ? formatPct(p / Math.max(1, r)) : "—",
      },
    ];
  }, [analytics]);

  const insights = useMemo(() => {
    const top = Object.entries(analytics.patternFrequency).sort((a, b) => b[1] - a[1])[0];
    const cohort = Object.entries(analytics.reactiveByRevenue).sort(
      (a, b) => (b[1].reactive / b[1].total || 0) - (a[1].reactive / a[1].total || 0)
    )[0];
    return { top, cohort };
  }, [analytics]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Executive Intelligence"
        title="Overview"
        description="Synthesis of decision debt patterns, assessment behavior, and conversion signals for the selected filters."
      />

      <div className="kpi-strip">
        <KpiCard label="Assessments" value={String(analytics.total)} hint="In filter window" />
        <KpiCard
          label="Completion rate"
          value={formatPct(analytics.completionRate)}
          hint={`${analytics.completed} completed`}
        />
        <KpiCard
          label="Avg. time to complete"
          value={analytics.averageTimeMs ? `${Math.round(analytics.averageTimeMs / 60000)}m` : "—"}
        />
        <KpiCard label="Report view / DL" value={formatPct(analytics.reportViewedRate)} />
        <KpiCard label="CTA CTR" value={formatPct(analytics.ctaRate)} />
        <KpiCard label="Retake rate" value={formatPct(analytics.retakeRate)} />
        <KpiCard
          label="Debt score shift (retakes)"
          value={`${analytics.repeatDelta > 0 ? "+" : ""}${analytics.repeatDelta}%`}
          hint="Latest vs first"
        />
      </div>

      <div className="grid-2">
        <Card title="Top patterns by frequency" subtitle="Primary pattern distribution across customers">
          <HorizontalBars items={patternBarItems} />
        </Card>
        <Card title="Severity mix" subtitle="Based on max pattern score per assessment">
          <DonutSeverity
            moderate={analytics.severityDist.Moderate}
            high={analytics.severityDist.High}
            critical={analytics.severityDist.Critical}
            total={analytics.completed}
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Assessment conversion funnel" subtitle="From start through monetization signals">
          <FunnelVertical stages={funnelStages} />
        </Card>
        <Card title="Intelligence insights" subtitle="Automated highlights from current data">
          <div className="insight-stack">
            <div className="insight-card">
              <span className="insight-card__tag">Pattern</span>
              <p>
                {insights.top
                  ? `Strongest signal: “${PATTERN_LABELS[insights.top[0]] || insights.top[0]}” appears on ${insights.top[1]} assessments.`
                  : "Not enough data to rank patterns yet."}
              </p>
            </div>
            <div className="insight-card">
              <span className="insight-card__tag">Dependency Loop by revenue</span>
              <p>
                {insights.cohort
                  ? `Highest share of “Dependency Loop” as primary: ${insights.cohort[0]} (${formatPct(insights.cohort[1].total ? insights.cohort[1].reactive / insights.cohort[1].total : 0)}).`
                  : "Revenue cohort data will appear as submissions grow."}
              </p>
            </div>
            <div className="insight-card">
              <span className="insight-card__tag">Engagement</span>
              <p>
                Report-to-CTA within 24h:{" "}
                <strong>{formatPct(analytics.reportToBookingIntentRate)}</strong>. Report → paid
                (telemetry): <strong>{formatPct(analytics.reportToPaidRate)}</strong>.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Quick actions" subtitle="Jump to operational views">
        <div className="quick-actions">
          <Link className="quick-action" to="/patterns">
            Pattern intelligence
          </Link>
          <Link className="quick-action" to="/funnel">
            Assessment funnel
          </Link>
          <Link className="quick-action" to="/submissions">
            Submissions explorer
          </Link>
        </div>
      </Card>

      <Card title="Recent submissions" subtitle={`${Math.min(5, submissions.length)} most recent`}>
        <ul className="activity-feed">
          {submissions.slice(0, 5).map((row) => (
            <li key={row.id ?? row.email + row.createdAt}>
              <span className="activity-feed__main">
                <strong>{row.full_name || row.email || "Unknown"}</strong>
                <span className="muted">
                  {PATTERN_LABELS[row.primary] || row.primary || "—"} · {row.overallPercent ?? "—"}%
                </span>
              </span>
              <span className="muted">{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ""}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
